import React, { useMemo, useState } from "react";
import { RadioButtonsList } from "../../../controls";
import {
  SCHEDULE_END_TYPES,
  schedulerEndTypeOptions,
  type ScheduleEndType,
} from "../SchedulerUtils";
import ScheduleEndOnDate from "./ScheduleEndOnDate";
import ScheduleEndNever from "./ScheduleEndNever";
import ScheduleEndAfter from "./ScheduleEndAfter";

// Union of the payloads the three end components emit via onChange.
// ScheduleEndOnDate may also emit a raw string (the selected date / "").
export type ScheduleEndChange =
  | { type: ScheduleEndType; data: ScheduleEndType }
  | { type: ScheduleEndType; data: number }
  | { type: ScheduleEndType; data: string }
  | string;

interface ScheduleEndComponentProps {
  onChange: (change: ScheduleEndChange) => void;
  initialValue?: number | string;
}

// The end components have divergent onChange/initialValue signatures; the
// shared boundary is widened here so a single onChange can drive all three.
const mapper = {
  [SCHEDULE_END_TYPES.NEVER]:
    ScheduleEndNever as React.FC<ScheduleEndComponentProps>,
  [SCHEDULE_END_TYPES.DATE]:
    ScheduleEndOnDate as React.FC<ScheduleEndComponentProps>,
  [SCHEDULE_END_TYPES.AFTER]:
    ScheduleEndAfter as React.FC<ScheduleEndComponentProps>,
} as Record<ScheduleEndType, React.FC<ScheduleEndComponentProps>>;

export interface ScheduleEndProps {
  disabled?: boolean;
  value?: number | string;
  type?: ScheduleEndType;
  onChange: (change: ScheduleEndChange) => void;
  allowedTypes?: ScheduleEndType[];
}

const ScheduleEnd: React.FC<ScheduleEndProps> = ({
  disabled = false,
  value,
  type,
  onChange,
  allowedTypes = [
    SCHEDULE_END_TYPES.NEVER,
    SCHEDULE_END_TYPES.DATE,
    SCHEDULE_END_TYPES.AFTER,
  ],
}) => {
  const options = useMemo(
    () =>
      schedulerEndTypeOptions.filter(({ name }) =>
        allowedTypes?.includes(name)
      ),
    [allowedTypes]
  );

  const [selectedType, setSelectedType] = useState<ScheduleEndType | undefined>(
    type || options[0]?.value
  );
  const EndComponent = mapper[selectedType as ScheduleEndType];

  return (
    <>
      <RadioButtonsList
        label="Ends"
        options={options}
        direction="row"
        value={selectedType}
        onChange={setSelectedType}
        data-testid={"scheduler-radio-button-list"}
        labelTop
        required
        disabled={disabled}
      />
      <EndComponent onChange={onChange} initialValue={value} />
    </>
  );
};

export default ScheduleEnd;
